import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, FormHelperText, Typography, Paper } from '@mui/material';
import { getAllCOE } from '../../../services/adminServices/CoeServices';
import { toast } from 'react-toastify';
import CoderDistributionTable from './CoderDistributionTable';
import { coderDistributionAdmin, coderDistributionMentor } from '../../../services/reportServices/coderDistributionServices';
import { getLabType } from '../../../services/mentorServices/MentorServices';

const CoderDistribution = () => {
    const [selection, setSelection] = useState({ labType: '', coe: '' });
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [disableButton, setDisableButton] = useState(true);
    const [error, setError] = useState({ labType: '', coe: '' });
    const [coes, setCoe] = useState([]);
    const [labTypes, setlabTypes] = useState([]);

    const userName = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'))?.name || 'User';
        } catch {
            return 'User';
        }
    }, []);

    useEffect(() => {
        getAllCOE()
            .then((response) => {
                const sortedLabs = response.data.data?.sort((a, b) => a.name?.localeCompare(b.name)) || [];
                setCoe(sortedLabs || []);
            })
            .catch((error) => toast.error(error?.response?.data?.message || 'Error fetching COE data'));
        getLabType()
            .then((response) => setlabTypes(response?.data?.data?.value || []))
            .catch((error) => toast.error(error?.response?.data?.message || 'Error fetching labTypes'));
    }, []);

    const handleSelectionChange = (event) => {
        const { name, value } = event.target;
        setSelection((prev) => ({ ...prev, [name]: value }));
        if (name === 'coe' || name === 'labType') setDisableButton(false);
        setError((prev) => ({ ...prev, [name]: '' }));
    };

    const fetchData = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem('user')) || {};
        const { coe, labType } = selection;
        if (!coe || !labType) {
            setError({
                labType: labType ? '' : 'Please select a labType',
                coe: coe ? '' : 'Please select a COE',
            });
            return;
        }
        setLoading(true);
        try {
            const response = (user.role === 'coe_report_admin' || user.role === 'coe_admin' || user.role === 'super_admin')
                ? await coderDistributionAdmin(coe, labType)
                : await coderDistributionMentor(coe, labType);
            setTableData(response.data.data);
            setShowTable(true);
            setDisableButton(true);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || error?.error);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    }, [selection]);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography
                sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#5f6368',
                    letterSpacing: '0.5px',
                    pt: 1,
                    pb: 2.5
                }}
            >
                Welcome {userName}! to the Coder Distribution Report.
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    width: { xs: '100%', md: 'fit-content' },
                    maxWidth: '100%',
                    mb: 2.5
                }}
            >
                <FormControl size="small" sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: 'none' } }} error={!!error.coe}>
                    <InputLabel id="coe-select-label">COE*</InputLabel>
                    <Select
                        labelId="coe-select-label"
                        id="coe-select"
                        name="coe"
                        value={selection.coe}
                        onChange={handleSelectionChange}
                        label="COE*"
                    >
                        {coes.map((coe) => (
                            <MenuItem key={coe.id} value={coe.id}>
                                {coe.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {error.coe && <FormHelperText>{error.coe}</FormHelperText>}
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: 'none' } }} error={!!error.labType}>
                    <InputLabel id="lab-type-select-label">Lab Type*</InputLabel>
                    <Select
                        labelId="lab-type-select-label"
                        id="lab-type-select"
                        name="labType"
                        value={selection.labType}
                        onChange={handleSelectionChange}
                        label="Lab Type*"
                    >
                        {labTypes.map((labType, index) => (
                            <MenuItem key={index} value={labType}>
                                {labType}
                            </MenuItem>
                        ))}
                    </Select>
                    {error.labType && <FormHelperText>{error.labType}</FormHelperText>}
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchData}
                    disabled={loading || !selection.coe || !selection.labType || disableButton}
                    sx={{
                        height: '40px',
                        px: 3.5,
                        borderRadius: '6px',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0 2px 6px rgba(25, 118, 210, 0.3)'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={22} color="inherit" /> : 'VIEW'}
                </Button>
            </Paper>

            <Box sx={{ width: '100%', mt: 1 }}>
                {showTable && <CoderDistributionTable data={tableData} loading={loading} />}
            </Box>
        </Box>
    );
};

export default CoderDistribution;
